import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { Nav, View } from "../nav";
import { GOOGLE_CLIENT_ID, getGuestId, getSigned, googleRedirectReady, loadGoogleScript, signInAvailable, signInWithGoogle, startGoogleRedirect, subscribeSigned } from "../account";
import { iosHomeScreenApp } from "../install-hint";
import { BackButton, Spinner, useApp, useHubSummaries } from "../ui";

export const useSigned = () => useSyncExternalStore(subscribeSigned, getSigned, getSigned);

/** Google refuses sign-in inside a WebView, so the Android app signs in with the phone's own Google account picker (a native plugin). */
const nativeGoogle = (): any => {
  const cap = (window as any).Capacitor;
  return cap?.isNativePlatform?.() ? cap.Plugins?.SocialLogin ?? null : null;
};

/** Google's own button; `onDone` runs once the relay has accepted the sign-in. */
export function GoogleButton({ onDone }: { onDone: () => void }) {
  const slot = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ kind: "idle" } | { kind: "working" } | { kind: "error"; message: string }>({ kind: "idle" });
  const native = nativeGoogle();
  const signInNative = async () => {
    setState({ kind: "working" });
    try {
      await native.initialize({ google: { webClientId: GOOGLE_CLIENT_ID } }); // the same client the relay checks the token against
      const r = await native.login({ provider: "google", options: { scopes: ["email", "profile"] } });
      const credential = r?.result?.idToken as string | undefined;
      if (!credential) return setState({ kind: "error", message: "Google didn't return a sign-in. Try again." });
      const res = await signInWithGoogle(credential);
      if (res.ok) onDone();
      else setState({ kind: "error", message: res.message });
    } catch (e) {
      const cancelled = /cancel/i.test(String((e as Error)?.message ?? e));
      setState(cancelled ? { kind: "idle" } : { kind: "error", message: "Couldn't sign in with Google. Try again." });
    }
  };
  useEffect(() => {
    if (native) return;
    let live = true;
    loadGoogleScript().then(
      () => {
        const id = (window as any).google?.accounts?.id;
        if (!live || !id || !slot.current) return;
        id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          // Chrome blocks the third-party cookies Google's button used to find your signed-in accounts with; FedCM is Chrome's way to
          // show the accounts from the browser profile, and itp_support does the same job for Safari's tracking prevention.
          use_fedcm_for_button: true,
          use_fedcm_for_prompt: true,
          itp_support: true,
          callback: async (r: { credential?: string }) => {
            if (!r.credential) return setState({ kind: "error", message: "Google didn't return a sign-in. Try again." });
            setState({ kind: "working" });
            const res = await signInWithGoogle(r.credential);
            if (res.ok) onDone();
            else setState({ kind: "error", message: res.message });
          },
        });
        id.renderButton(slot.current, { theme: "outline", size: "large", text: "continue_with", shape: "pill", width: 280 });
      },
      () => live && setState({ kind: "error", message: "Couldn't load Google sign-in. Check your connection." }),
    );
    return () => {
      live = false;
    };
  }, [onDone, native]);
  // The Home Screen app on an iPhone: Google's popup has no accounts there, so use the full-page sign-in when the relay says it is set up.
  const [redirect, setRedirect] = useState(false);
  useEffect(() => {
    if (native || !iosHomeScreenApp()) return;
    let live = true;
    void googleRedirectReady().then((ok) => live && setRedirect(ok));
    return () => {
      live = false;
    };
  }, [native]);
  return (
    <>
      {redirect ? <RedirectSignIn /> : null}
      {native ? (
        <button className="btn secondary" onClick={() => void signInNative()} hidden={state.kind === "working"}>
          Continue with Google
        </button>
      ) : (
        <div ref={slot} className="google-slot" hidden={state.kind === "working" || redirect} />
      )}
      {state.kind === "working" && <Spinner size={22} />}
      {state.kind === "error" && (
        <div className="err-text" role="alert">
          {state.message}
        </div>
      )}
    </>
  );
}

/**
 * Offered while connecting a computer. Signing in is optional (a quiet "Skip" in the corner) but a phone without an account
 * is limited to one computer and, on it, one running session. `required` hides Skip: pairing a second computer.
 */
export function SignInScreen({ nav, then, required }: { nav: Nav; then: View; required?: boolean }) {
  const signed = useSigned();
  const go = () => nav.replace(then);
  // Already signed in (or sign-in isn't part of this build): nothing to ask.
  useEffect(() => {
    if (signed || !signInAvailable()) nav.replace(then);
  }, [signed, then, nav]);
  return (
    <div className="screen" data-screen="signin">
      <div className="safe-top">
        <div className="topbar pair">
          <BackButton onClick={nav.back} />
          <div className="title">Sign in</div>
        </div>
      </div>
      <div className="scroll">
        <div className="content centered" style={{ padding: "10vh 24px 40px", gap: 14 }}>
          <div className="big">Sign in</div>
          <div className="copy">Signing in keeps your computers together under one account, on every phone. Brocoded only learns your name and email, never your sessions, commands or files.</div>
          <GoogleButton onDone={go} />
          {!required && (
            <>
              <div className="or-line">or</div>
              <button className="btn secondary guest-btn" onClick={go}>
                Not now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** What each connected computer says about its own account, live (a computer can be signed in while this phone is not). */
/**
 * The Home Screen app on an iPhone can't show Google's accounts in a popup: its own button takes the whole window to Google's sign-in page
 * and comes back here signed in. Only shown when the relay says Google accepts that return address.
 */
function RedirectSignIn() {
  return (
    <button className="btn primary" onClick={() => startGoogleRedirect()} data-redirect-signin>
      Continue with Google
    </button>
  );
}

export function useComputerAccounts(): { signedIn: boolean; hint: string | null }[] {
  const { hub } = useApp();
  const summaries = useHubSummaries(hub);
  const [, bump] = useState(0);
  useEffect(() => {
    const off = summaries.map((s) => hub.get(s.computer.id)?.subscribe(() => bump((n) => n + 1)));
    return () => off.forEach((f) => f?.());
  }, [hub, summaries]);
  return summaries.flatMap((s) => {
    const a = hub.get(s.computer.id)?.getSnapshot().account;
    return a ? [{ signedIn: a.signedIn, hint: a.hint }] : [];
  });
}

/** A compact card at the top of the home screen: who you are, and a way into the Account screen. */
export function AccountRow({ nav }: { nav: Nav }) {
  const signed = useSigned();
  const computers = useComputerAccounts();
  if (!signInAvailable()) return null;
  const linked = computers.filter((c) => c.signedIn);
  const initial = (signed?.name ?? signed?.email ?? "G").trim().charAt(0).toUpperCase();
  void linked; // a computer linked to an account doesn't change what this phone may do: the plan is the phone's
  const title = signed ? signed.name ?? signed.email : "Guest";
  const sub = signed
    ? `${signed.name ? signed.email + " · " : ""}Early access from ur bro 😉`
    : `${getGuestId()} · only on this phone`;
  return (
    <button className="account-card" data-state={signed ? "signed-in" : "guest"} onClick={() => nav.push({ name: "account" })}>
      <span className={"avatar" + (signed ? "" : " guest")} aria-hidden>
        {signed ? initial : "G"}
      </span>
      <span className="account-text">
        <strong>{title}</strong>
        <span>{sub}</span>
      </span>
      <span className="account-go" aria-hidden>
        ›
      </span>
    </button>
  );
}
